import { clone, has } from "ramda";
import { escapeString, isUndefinedOrEmptyArray, unescapeString } from "widgets/utils";

/**
 https://github.com/jirutka/rsql-parser
 Syntax:
    https://github.com/jirutka/rsql-parser#grammar-and-semantic

 EBNF Hint: (http://www.cl.cam.ac.uk/~mgk25/iso-14977.pdf)
 =       definition of a rule, assigns right side expression to left side symbol,
 ;       terminates the expression
 -       set minus operator, excludes right side symbols from left side symbols
 ,       concatenate left and right expression
 { x }   start and end a repeated expression, the repeat count can be 0!
 ( x )   start and end a group
 |       separates alternatives in a group
 [ x ]   optional parts
 ? x ?   x is the description of one special-sequence-symbol

 EOF    end of file
 ALPHA  a char in [a-zA-Z]

 // rsql syntax

 input          = or, EOF;
 or             = and, { "," , and };
 and            = constraint, { ";" , constraint };
 constraint     = ( group | comparison );
 group          = "(", or, ")";

 comparison     = selector, comparison-op, arguments;

 selector       = unreserved-str;

 comparison-op  = comp-fiql | comp-alt;
 comp-fiql      = ( ( "=", { ALPHA } ) | "!" ), "=";
 comp-alt       = ( ">" | "<" ), [ "=" ];

 arguments      = ( "(", value, { "," , value }, ")" ) | value;
 value          = unreserved-str | double-quoted | single-quoted;

 unreserved-str = unreserved, { unreserved }
 single-quoted  = "'", { ( escaped | all-chars - ( "'" | "\" ) ) }, "'";
 double-quoted  = '"', { ( escaped | all-chars - ( '"' | "\" ) ) }, '"';

 reserved       = '"' | "'" | "(" | ")" | ";" | "," | "=" | "!" | "~" | "<" | ">";
 unreserved     = all-chars - reserved - " ";
 escaped        = "\", all-chars;
 all-chars      = ? all unicode characters ?;

 ALPHA          = ? char in 'A'..'Z' | 'a'..'z' ?  == ( .alpha. | .ALPHA. )

 .ALPHA.        = "A" | "B" | "C" | "D" | "E" | "F"
 | "G" | "H" | "I" | "J" | "K" | "L" | "M"
 | "N" | "O" | "P" | "Q" | "R" | "S" | "T"
 | "U" | "V" | "W" | "X" | "Y" | "Z";

 .alpha.        = "a" | "b" | "c" | "d" | "e" | "f"
 | "g" | "h" | "i" | "j" | "k" | "l" | "m"
 | "n" | "o" | "p" | "q" | "r" | "s" | "t"
 | "u" | "v" | "w" | "x" | "y" | "z";

 // additional rules by us (genedata)
 All values are enclosed in quoted strings

 Comparison-op additionally includes '<' '>'
 and operators matching =([a-z]+)= as defined by the standard

 Typical comparison-ops are:
 '==' '!=' '<=' '>=' '<' '>'
 '=gt='  '=ge=' '=le=' '=lt='
 '=in='  '=out=' '=between='

  You can define your own operators (see syntax below)
  They have to comply to the below syntax:  (Example: =between=)

  comparison-op  = comp-fiql | comp-alt;
  comp-fiql      = ( ( "=", { ALPHA } ) | "!" ), "=";
  comp-alt       = ( ">" | "<" ), [ "=" ];


 */

export type RsqlFilterConstraint = IRsqlFilterTree | IRsqlComparisonExpr;

export type IRsqlFilterTree = Record<string, RsqlFilterConstraint[]>;

export interface IRsqlComparisonExpr {
  key: string;
  operator: string;
  value: string | number | string[];
  value2?: any;
}

export const rxReservedChar = /^\s*(["'();,=!~<> ])/;
// matches the next reserved char
//  tolerates and ignores leading spaces
export const rxSelector = /^\s*([^"'();,=!~<> ]+)/;
// matches the selector part before the comparison operator
//  tolerates and ignores leading spaces
export const rxCompOp = /^\s*(((!|=[a-z]+)=)|(<=?|>=?|==))\s*/;
// matches the extended comparison-op's

const listSep = ",";
const quoteChar = "'";
export const escapeChar = "\\";

// same list of operators on server side
export const SEARCH_OPERATORS = {
  // emulated operators
  STARTS_WITH: "startsWith",
  ENDS_WITH: "endsWith",
  CONTAINS: "contains",
  DOES_NOT_CONTAIN: "doesNotContain",
  BETWEEN: "=between=",
  // real rsql operators
  EQUAL: "==",
  NOT_EQUAL: "!=",
  GREATER_THAN: ">",
  GREATER_THAN_OR_EQUAL: ">=",
  LESS_THAN: "<",
  LESS_THAN_OR_EQUAL: "<=",
  // list operators
  IN: "=in=",
  OUT: "=out=",
  PRESENT: "=present=",
  ABSENT: "=absent=",
};

// real rsql operators known to the middle tier
//   uninplemented are =gt=, =ge=, =le=, =lt=
const listOfOperators = [
  "==",
  "!=",
  ">",
  ">=",
  "<",
  "<=",
  // list operators
  "=in=",
  "=out=",
  "=present=",
  "=absent=",
  "=between="
];

export const CONSTRAINT_OPERATORS = {
  AND: "and",
  OR: "or",
};

const mapSymbol2LogicOp = {
  ",": "or",
  ";": "and",
};

interface IParserInfo {
  found: number; // 1 OK, 0 error
  lastChar: number; // last consumed char
  text?: string; // exists when found is 1
  match?: RegExpMatchArray;
  result?: RsqlFilterConstraint;
  message?: string;
}

interface IParserState {
  expr: string;
  pos: number;
}

class RsqlParserError extends Error {
  state: IParserState;

  // @ts-ignore
  constructor(message, state: IParserState) {
    super(message);
    this.name = "RsqlParserError";
    this.state = state;
  }
}

class RsqlParser {
  // API: entry for parsing
  parse(expression: string): RsqlFilterConstraint | undefined {
    const ret: IParserInfo | undefined = this.tryOrList(expression);
    if (ret) {
      if (ret.lastChar < expression.length && !expression.substring(ret.lastChar + 1).match(/^\s*$/)) {
        return undefined;
      }

      return ret.result;
    }
    return undefined;
  }

  private isEmptyText(text: string): boolean {
    return !text || text.length === 0 || text.trim().length === 0;
  }

  private textMatch(text: string, lastChar: number): IParserInfo {
    return { text, lastChar, found: 1 };
  }

  // private regexMatch(match: RegExpMatchArray, lastChar: number): IParserInfo {
  //   return { lastChar, found: !!match ? 1 : 0, match };
  // }

  private validResult(result: RsqlFilterConstraint, lastChar: number): IParserInfo {
    return { result, lastChar, found: 1 };
  }

  private noMatch(text: string | undefined, lastChar: number, message = ""): IParserInfo {
    return { text, lastChar, found: 0, message };
  }

  private raiseNoMatch(_text: string | null, lastChar: number, message = ""): IParserInfo {
    throw new RsqlParserError(message, { pos: lastChar, expr: "" });
    //return {text, lastChar, found: 0, message};
  }

  private consume(state: IParserState, ret: IParserInfo, deltaPos: number | null = null) {
    state.pos += deltaPos ? deltaPos : ret.lastChar + 1;
    state.expr = state.expr.substring(deltaPos ? deltaPos : ret.lastChar + 1);
  }

  private tryOrList(expression: string): IParserInfo | undefined {
    // accept or-expression
    // rule:   or             = and, { COMMA , and };

    //   constraint-lists are enclosed in parentheses, single values do not need parentheses
    //   rule: constraint-lists = '(', group of constraints separated by logic-sep and terminated by closing paren, ')'
    //         or
    //         constraint
    let ret: IParserInfo | undefined;

    try {
      const openParen: IParserInfo = this.matchGroupStart(expression);

      if (openParen.found) {
        ret = this.parseGroup(expression);
      } else {
        ret = this.parseComparison(expression);
      }
    } catch (e) {
      ret = undefined;
      /* empty */
    }

    return ret;
  }

  private matchNonWs(expression: string): IParserInfo {
    const m = expression.match(/^\s*(\S)/);

    if (!m) {
      return this.raiseNoMatch(null, 0, "no <non-white-space> ahead");
    }

    return this.textMatch(m[1], m[0].length - 1);
  }

  // private matchLogicSep(expression: string): IParserInfo {
  //   const m = expression.match(/^\s*([;,])\s*/); // TBD: 'and' | 'or' not accepted
  //   if (!m) {
  //     return this.raiseNoMatch(null, 0, "no <logic-sep> ahead");
  //   }
  //   return this.textMatch(m[1], m[0].length - 1);
  // }

  private optionalListSep(expression: string): IParserInfo {
    const m = expression.match(/^\s*(,)\s*/);

    if (!m) {
      return this.noMatch(undefined, 0, "no <value-sep>"); // return 'no-match' because of optional
    }

    return this.textMatch(m[1], m[0].length - 1);
  }

  private matchOptionalGroupStart(expression: string): IParserInfo {
    const m = expression.match(/^\s*(\()\s*/);

    if (!m) {
      return this.noMatch(undefined, 0, "no opening paren for <group>");
    }

    return this.textMatch(m[1], m[0].length - 1);
  }

  private matchGroupStart(expression: string): IParserInfo {
    const m = expression.match(/^\s*(\()\s*/);

    if (!m) {
      return this.raiseNoMatch(null, 0, "no opening paren for <group>");
    }

    return this.textMatch(m[1], m[0].length - 1);
  }

  private matchGroupEnd(expression: string): IParserInfo {
    const m = expression.match(/^\s*(\))\s*/);

    if (!m) {
      return this.raiseNoMatch(null, 0, "no closing paren for <group>");
    }

    return this.textMatch(m[1], m[0].length - 1);
  }

  requireSymbol(state: IParserState, info: IParserInfo, failureMessage: string): IParserInfo {
    if (!info.found) {
      throw new RsqlParserError(failureMessage, state);
    }

    this.consume(state, info);

    return info;
  }

  requireGroupStart(state: IParserState) {
    this.requireSymbol(state, this.matchGroupStart(state.expr), "no opening paren for <group>");
  }

  requireGroupEnd(state: IParserState) {
    this.requireSymbol(state, this.matchGroupEnd(state.expr), "no closing paren for <group>");
  }

  requireValueListStart(state: IParserState) {
    this.requireSymbol(state, this.matchGroupStart(state.expr), "no opening paren for <value-list>");
  }

  requireValueListEnd(state: IParserState) {
    this.requireSymbol(state, this.matchGroupEnd(state.expr), "no closing paren for <value-list>");
  }

  requireSelector(state: IParserState): IParserInfo {
    return this.requireSymbol(state, this.matchSelectorExpr(state.expr), "expected <selector>");
  }

  requireOperator(state: IParserState): IParserInfo {
    return this.requireSymbol(state, this.matchOperatorExpr(state.expr), "expected <operator>");
  }

  requireQuotedString(state: IParserState): IParserInfo {
    return this.requireSymbol(state, this.matchQuotedString(state.expr), "expected <quoted-string>");
  }

  private parseGroup(expression: string, level = 0): IParserInfo {
    // accept constraint-expression
    // rule: group          = OPEN_PAREN, or-list, CLOSE_PAREN;
    let logicOp: string | undefined = undefined; // initially undefined until the logic-op is found past the first constraint
    const state: IParserState = {
      pos: 0,
      expr: expression,
    };

    this.requireGroupStart(state);

    /// HINT: simplified rules: a logic-list is always embedded in a group (with parenthesis)
    //  and each logic-list has only one kind of logic-op but they can be nested.
    //
    // group            =  "(", logic-list, ")";
    // constraint-list  =  constraint,  { logic-op , constraint };
    // constraint       =  ( group | comparison );
    // logic-op         =  ( ',' | ';' )

    const retList: RsqlFilterConstraint[] = [];
    let fetchNext = true;
    let foundLogicOp = false;
    let nextNonWs = this.matchNonWs(state.expr);

    while (nextNonWs && nextNonWs.found && nextNonWs.text !== ")") {
      if (nextNonWs.text === "(") {
        // parse a nested group
        const ret = this.parseGroup(state.expr, level + 1);
        this.consume(state, ret, ret.lastChar);
        if (ret.result) {
          retList.push(ret.result);
        }
      } else {
        // parse a comparison
        const ret: IParserInfo = this.parseComparison(state.expr);

        this.consume(state, ret, ret.lastChar);

        // push on stack or merge 'between' to an existing entry
        this.mergeFilterOperations(retList, ret.result as IRsqlComparisonExpr);
      }

      // expect  <logic-op> or <closing-paren>
      fetchNext = true;
      foundLogicOp = false;

      while (fetchNext) {
        nextNonWs = this.matchNonWs(state.expr);
        // either  logic-op or  closing-paren must be there
        fetchNext = false;

        if (nextNonWs.text === ")") {
          // terminate loop through while condition
        } else if (!foundLogicOp && (nextNonWs.text === "," || nextNonWs.text === ";")) {
          foundLogicOp = true; // accept only one logicOp per list continuation
          if (retList.length === 1) {
            logicOp = nextNonWs.text;
          } else if (logicOp !== nextNonWs.text) {
            // wrong operator, maybe wrong nesting of groups
            return this.raiseNoMatch(null, state.pos, "wrong <logic-op>, maybe wrong nesting of groups");
          }

          this.consume(state, nextNonWs);

          // trigger fetching a new nextNonWs
          fetchNext = true;
        }
      }
    }

    this.requireGroupEnd(state);

    // groups must have at least one member
    if (retList.length === 0) {
      return this.raiseNoMatch(null, state.pos, "empty <constraint-list>");
    }

    // simplify return value:
    //  *  for a single entry  return the entry instead of the list with one entry
    //  *  TBD ?? combine nested lists with identical logicOp
    if (retList.length === 1) {
      return this.validResult(retList[0], state.pos);
    } else {
      const result: RsqlFilterConstraint = {};

      // @ts-ignore
      result[mapSymbol2LogicOp[logicOp]] = retList;

      return this.validResult(result, state.pos);
    }
  }

  private matchSelectorExpr(expression: string): IParserInfo {
    const m = expression.match(rxSelector);

    if (!m) {
      return this.raiseNoMatch(null, 0, "expected <selector>");
    }

    const key: string = m[0].trim();

    return this.textMatch(key, m[0].length - 1);
  }

  matchOperatorExpr(expression: string): IParserInfo {
    const m = expression.match(rxCompOp);

    if (!m) {
      return this.raiseNoMatch(null, 0, "expected <comp-op>");
    }

    const key: string = m[1];

    return this.textMatch(key, m[0].length - 1);
  }

  matchQuotedString(text: string): IParserInfo {
    // rule: double-quoted | single-quoted
    const m = text.match(/^\s*(['"])/);

    if (!m) {
      // throw Error('rsql error - does not start with a quoted string '+ text);
      return this.raiseNoMatch(null, 0, "expected <start-quote>");
    }

    const startQuote: string = m[1];
    let nextQuote = text.indexOf(startQuote, 1);
    let nextEsc = text.indexOf(escapeChar, 1);
    let pos = nextQuote > -1 && nextEsc > -1 ? Math.min(nextEsc, nextQuote) : nextEsc === -1 ? nextQuote : nextEsc;

    while ((nextQuote > -1 || nextEsc > -1) && text.charAt(pos) !== startQuote) {
      if (pos === nextEsc) {
        nextEsc = text.indexOf(escapeChar, nextEsc + 2);

        if (text.charAt(pos + 1) === startQuote) {
          nextQuote = text.indexOf(startQuote, pos + 2);
        }
      }
      pos = nextQuote > -1 && nextEsc > -1 ? Math.min(nextEsc, nextQuote) : nextEsc === -1 ? nextQuote : nextEsc;
    }

    if (pos > -1) {
      // found  quote which is not escaped
      return this.textMatch(text.substring(1, pos), pos);
    }

    // no terminal quote
    return this.raiseNoMatch(null, pos);
    // throw Error('rsql error - does not end with the same quote sign ('+ startQuote + ') ' + text);
  }

  private parseComparison(expression: string): IParserInfo {
    // accept constraint-expression
    // rule: comparison     = selector, comparison-op, arguments;
    const state: IParserState = {
      pos: 0,
      expr: expression,
    };
    // match selector
    // rule:   selector       = unreserved-str;
    let ret: IParserInfo = this.requireSelector(state);
    // @ts-ignore
    const key: string = ret.text;

    if (this.isEmptyText(state.expr)) {
      return this.raiseNoMatch(null, state.pos, "no text after <selection>");
    }

    // match operator
    // rule:  comparison-op  = comp-fiql | comp-alt;
    //
    ret = this.requireOperator(state);
    const operator: string | undefined = ret.text;

    if (!operator || !listOfOperators.includes(operator)) {
      // undefined operator
      return this.raiseNoMatch(null, state.pos, "unknown <comp-op> found");
    }

    if (this.isEmptyText(state.expr)) {
      return this.raiseNoMatch(null, state.pos, "no text after <comp-op>");
    }

    // match value
    // rule:  value          = unreserved-str | double-quoted | single-quoted;
    // HINT: simplification, assume all values are in single or double quotes, even numbers are in strings!
    //   the 'unreserved-str' branch in the above rule is not implemented
    //   the content can contain escaped chars (for any char not just for quotes)
    let value: string | string[];

    if (
      [SEARCH_OPERATORS.IN, SEARCH_OPERATORS.OUT, SEARCH_OPERATORS.ABSENT, SEARCH_OPERATORS.PRESENT, SEARCH_OPERATORS.BETWEEN].includes(operator)
    ) {
      // values must be in a list
      // HINT: simplification, assume the list is not replaced by a single quoted value

      const valueList: string[] = [];

      // explore if there is an opening parenthesis (look ahead)
      const symbol: IParserInfo = this.matchOptionalGroupStart(state.expr);

      if (symbol.found) {
        this.requireValueListStart(state);
        // collect list of quoted strings until no <list-sep> found
        let m: IParserInfo | null = this.requireQuotedString(state);

        while (m && m.found) {
          valueList.push(unescapeString(m.text as string));
          // expect <list-sep> or <closing-paren>
          const nextListSep = this.optionalListSep(state.expr);

          if (nextListSep.found) {
            this.consume(state, nextListSep);
            m = this.requireQuotedString(state);
          } else {
            m = null;
          }
        }

        this.requireValueListEnd(state);
      } else {
        const m = this.requireQuotedString(state);
        valueList.push(unescapeString(m.text as string));
      }
      if (valueList.length === 0) {
        return this.raiseNoMatch(null, state.pos, "empty <value-list>");
      }

      value = valueList;
    } else {
      const m = this.requireQuotedString(state);
      value = unescapeString(m.text as string);
    }

    const comparison: IRsqlComparisonExpr = this.translateCustomFilterOperations({
      key,
      operator,
      value,
    });

    return this.validResult(comparison, state.pos);
  }

  private translateCustomFilterOperations(fo: IRsqlComparisonExpr): IRsqlComparisonExpr {
    if (typeof fo.value === "string") {
      const hasTrailingStar: boolean = fo.value.endsWith("*");
      // * cannot be escaped in Rsql-QueryDSL.
      const hasLeadingStar: boolean = fo.value.startsWith("*");

      // beware: hasTrailingStar versus operator starts_with
      //    operator starts_with means the value has a _trailing_ *.
      //    It doesn't mean the value starts with a *.
      // beware: the raw value can end in an escaped *
      if (hasTrailingStar && hasLeadingStar) {
        const equalsOperator: boolean = fo.operator === "==";

        fo.operator = equalsOperator ? SEARCH_OPERATORS.CONTAINS : SEARCH_OPERATORS.DOES_NOT_CONTAIN;
        fo.value = fo.value.substring(1, fo.value.length - 1);
      } else if (hasTrailingStar) {
        fo.operator = SEARCH_OPERATORS.STARTS_WITH;
        fo.value = fo.value.substring(0, fo.value.length - 1);
      } else if (hasLeadingStar) {
        fo.operator = SEARCH_OPERATORS.ENDS_WITH;
        fo.value = fo.value.substring(1, fo.value.length);
      }
    }

    return fo;
  }

  // combine two filters for 'between'     field > a and field < b   =>   field between a and b
  private mergeFilterOperations(filterOperations: RsqlFilterConstraint[], newFilterOperation: IRsqlComparisonExpr) {
    const existingFilterOperationWithSameKey: any = filterOperations
      .filter((fo: RsqlFilterConstraint) => !isFilterTree(fo))
      .find((fo: RsqlFilterConstraint) => (fo as IRsqlComparisonExpr).key === newFilterOperation.key);

    if (
      existingFilterOperationWithSameKey &&
      this.isBetweenOperator(existingFilterOperationWithSameKey, newFilterOperation)
    ) {
      existingFilterOperationWithSameKey.operator = SEARCH_OPERATORS.BETWEEN;
      existingFilterOperationWithSameKey.value2 = newFilterOperation.value;
    } else {
      filterOperations.push(newFilterOperation);
    }
  }

  private isBetweenOperator(fo1: IRsqlComparisonExpr, fo2: IRsqlComparisonExpr) {
    return (
      //    field >= lower_value && field <= upper_value
      (fo1.operator === SEARCH_OPERATORS.GREATER_THAN_OR_EQUAL &&fo2.operator === SEARCH_OPERATORS.LESS_THAN_OR_EQUAL)
    );
  }
}

export function parse(expression: string | undefined | null): RsqlFilterConstraint | undefined | null {
  // accepts INPUT
  // return undefined  or the parser tree
  if (!expression) {
    return undefined;
  }

  let text = expression.trim();

  if (!text) {
    return undefined;
  }

  if (!text.match(/^\s*\(.*\)\s*$/)) {
    text = "(" + expression + ")";
  }

  let result: RsqlFilterConstraint | undefined;

  try {
    const parser = new RsqlParser();

    result = parser.parse(text);
  } catch (e) {
    return null;
  }

  return result;
}

// operations on the parser result
// API
export function isOperationUnit(filterConstraint: RsqlFilterConstraint): boolean {
  return !isFilterTree(filterConstraint) && has("operator")(filterConstraint);
}

// API
export function isFilterTree(filterConstraint: any): boolean {
  if (!filterConstraint) {
    return false;
  }

  return !!filterConstraint[CONSTRAINT_OPERATORS.AND] || !!filterConstraint[CONSTRAINT_OPERATORS.OR];
}

// API
export function constraintCount(filterConstraint: any): number {
  if (!filterConstraint) {
    return 0;
  }

  if (isFilterTree(filterConstraint)) {
    const list = filterConstraint[CONSTRAINT_OPERATORS.AND] || filterConstraint[CONSTRAINT_OPERATORS.OR];

    return list.length;
  }

  return 1;
}

// API
export function toFilterTree(filter: RsqlFilterConstraint, op = "or"): IRsqlFilterTree {
  if (isFilterTree(filter)) {
    return filter as IRsqlFilterTree;
  }

  const ret: IRsqlFilterTree = {};

  ret[op] = [filter];

  return ret;
}

// API
export function toComparisonArray(filter: RsqlFilterConstraint | null | undefined): RsqlFilterConstraint[] {
  if (!filter) {
    return [];
  }

  if (isFilterTree(filter)) {
    const filterTree = filter as IRsqlFilterTree;
    return has(CONSTRAINT_OPERATORS.AND, filterTree)
      ? filterTree[CONSTRAINT_OPERATORS.AND]
      : filterTree[CONSTRAINT_OPERATORS.OR];
  }

  return [filter];
}

// API
export function getLogicOp(filter: RsqlFilterConstraint): string | undefined {
  if (!filter || !isFilterTree(filter)) {
    return undefined;
  }

  return has(CONSTRAINT_OPERATORS.AND)(filter) ? CONSTRAINT_OPERATORS.AND : CONSTRAINT_OPERATORS.OR;
}

// called from advanced-search and other places
//
export function parseFilterTree(expression: string, op = "or"): IRsqlFilterTree {
  const filter: RsqlFilterConstraint | undefined | null = parse(expression);

  if (filter && isOperationUnit(filter)) {
    return toFilterTree(filter, op);
  }

  return filter as IRsqlFilterTree;
}

export function parseComparison(expression: string): IRsqlComparisonExpr | null {
  const filter: RsqlFilterConstraint | undefined | null = parse(expression);

  if (filter && !isOperationUnit(filter)) {
    // @ts-ignore
    return filter[getLogicOp(filter)][0];
  }

  return filter as IRsqlComparisonExpr;
}

function toQuotedStringValue(text: any): string {
  // Always put quotes around the value, does not hurt for numbers or booleans ...
  // For strings escape single quotes
  if (typeof text === "string") {
    // replace single and double quote with \' or \"
    return quoteChar + escapeString(text) + quoteChar;
  }

  return quoteChar + text + quoteChar;
}

export function encodeFilterOperationUnit(operation: IRsqlComparisonExpr): string {
  const operator: string = operation.operator;
  let filterValue: any;
  if (
    operator === SEARCH_OPERATORS.IN ||
    operator === SEARCH_OPERATORS.OUT ||
    operator === SEARCH_OPERATORS.PRESENT ||
    operator === SEARCH_OPERATORS.ABSENT ||
    operator === SEARCH_OPERATORS.BETWEEN
  ) {
    // if operator is IN/OUT the value is actually an Array (see component AdvancedSearchSelect)
    if (!isUndefinedOrEmptyArray(operation.value)) {
      filterValue = `(${(operation.value as string[])
        .map((value: string) => toQuotedStringValue(value))
        .join(listSep)})`;
    }

  } else {
    filterValue = toQuotedStringValue(operation.value);
  }

  return `${operation.key}${operator}${filterValue}`;
}

function translateFilterOperations(filterConstraints: RsqlFilterConstraint[]): RsqlFilterConstraint[] {
  const translatedOperations: IRsqlComparisonExpr[] = [];

  filterConstraints.forEach((constraint: RsqlFilterConstraint)  => {
    const op = constraint as IRsqlComparisonExpr;
    const operation = clone(op);

    switch (operation.operator) {
      case SEARCH_OPERATORS.CONTAINS:
        {
          translatedOperations.push({
            ...operation,
            operator: "==",
            value: `*${operation.value}*`,
          });
        }
        break;
      case SEARCH_OPERATORS.DOES_NOT_CONTAIN:
        {
          translatedOperations.push({
            ...operation,
            operator: "!=",
            value: `*${operation.value}*`,
          });
        }
        break;
      case SEARCH_OPERATORS.STARTS_WITH:
        {
          translatedOperations.push({
            ...operation,
            operator: "==",
            value: `${operation.value}*`,
          });
        }
        break;
      case SEARCH_OPERATORS.ENDS_WITH:
        {
          translatedOperations.push({
            ...operation,
            operator: "==",
            value: `*${operation.value}`,
          });
        }
        break;
      case SEARCH_OPERATORS.BETWEEN:
        // special case where we actually need to expand to 2 values,
        // which are not already in an array.
        translatedOperations.push(
          {
            ...operation,
            operator: SEARCH_OPERATORS.BETWEEN,
            value:
              typeof operation.value === "number"
                ? [ operation.value,  operation.value2 ]
                : [ `${operation.value}`, `${operation.value2}` ]
          }
        );
        break;
      default:
        translatedOperations.push(operation);
    }
  });

  return translatedOperations;
}

export function encodeFilterOperations(filterConstraint: IRsqlFilterTree): string | null {
  // assert: isFilterTree, has more than one entry
  if (!filterConstraint) {
    return null;
  }

  const andClause: RsqlFilterConstraint[] = filterConstraint[CONSTRAINT_OPERATORS.AND];
  const orClause: RsqlFilterConstraint[] = filterConstraint[CONSTRAINT_OPERATORS.OR];
  const list: RsqlFilterConstraint[] = andClause || orClause;

  return encodeFilterArrays(list).join(andClause ? ";" : ",");
}

export function encodeFilterArrays(filterConstraints: RsqlFilterConstraint[]): string[] {
  const translatedConstraints: RsqlFilterConstraint[] = translateFilterOperations(filterConstraints);

  return translatedConstraints.map((filterConstraint: RsqlFilterConstraint) => {
    if (isFilterTree(filterConstraint)) {
      return "(" + encodeFilterOperations(filterConstraint as IRsqlFilterTree) + ")";
    } else {
      return encodeFilterOperationUnit(filterConstraint as IRsqlComparisonExpr);
    }
  });
}

// API METHOD: THIS IS THE ONE THAT SHOULD BE USED (others are exported for testing purpose)
export function encodeFilter(filterConstraints: RsqlFilterConstraint): string | null {
  if (!filterConstraints) {
    return null;
  }

  // clone since we dont want to mutate the original values (might be bound to redux-form for example)
  const filter: RsqlFilterConstraint = clone(filterConstraints);

  // specific case where the input constraint is only a single operationUnit (no AND, no OR, nothing)
  if (!isFilterTree(filter)) {
    const singleOperation: IRsqlComparisonExpr = filter as IRsqlComparisonExpr;
    const filterOperations: IRsqlComparisonExpr[] = translateFilterOperations([
      singleOperation,
    ]) as IRsqlComparisonExpr[];

    if (filterOperations.length === 1) {
      // e.g. the between case splits into 2 operation units
      return encodeFilterOperationUnit(filterOperations[0]);
    } else {
      return encodeFilter({ [CONSTRAINT_OPERATORS.AND]: filterOperations });
    }
  }

  const text: string | null = encodeFilterOperations(filter as IRsqlFilterTree);

  if (constraintCount(filter) > 1) {
    return "(" + text + ")";
  }

  return text;
}

export function isIRsqlComparisonExpr(obj: RsqlFilterConstraint): boolean {
  return Object.prototype.hasOwnProperty.call(obj, "operator");
}
export function isIRsqlFilterTree(obj: RsqlFilterConstraint): boolean {
  return !isIRsqlComparisonExpr(obj);
}

